"use client"

import React, { useSyncExternalStore } from "react"
import cx from "classnames"
import {
  LIVE_STATISTICS_SUPPORTED_SPORTS,
  LIVE_STATISTICS_SUPPORTED_PROVIDERS,
  useGameState,
} from "@azuro-org/sdk"
import { getProviderFromId } from "@azuro-org/toolkit"
import { openModal } from "@locmod/modal"
import { getGameDateTime } from "helpers/getters"
import { liveStatisticsGameIdStore } from "helpers/stores"

import { OpponentLogo } from "components/dataDisplay"
import { Href } from "components/navigation"
import { Icon, LiveLabel } from "components/ui"
import { MarketsSkeleton } from "compositions/events/Markets/Markets"
import TimeValue from "compositions/TimeValue"
import OvertimeMarkets from "../Markets/OvertimeMarkets"

export const OvertimeGameSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => {
  const rootClassName = cx(
    "flex mb:flex-col ds:items-center justify-between py-2 px-4 bg-accent-grey-850 last-of-type:rounded-b-md",
    className
  )

  return (
    <div className={rootClassName}>
      <div className="flex items-center">
        <div className="bone size-7 -mt-2 rounded-full" />
        <div className="bone size-7 -mb-2 -ml-2 z-20 rounded-full" />
        <div className="ml-3">
          <div className="mb-[2px] flex items-center">
            <div className="bone h-4 w-8 mr-1 rounded-md" />
            <span className="bone h-[0.875rem] w-20 rounded-md" />
          </div>
          <div className="bone h-4 w-24 rounded-md" />
        </div>
      </div>
      <div className="w-full max-w-[26.25rem] mb:mt-2">
        <MarketsSkeleton />
      </div>
    </div>
  )
}

type OvertimeGameProps = {
  className?: string
  leagueUrl: string
  game: any
  withTopRadius?: boolean
  isUnique?: boolean
}

const OvertimeGame: React.FC<OvertimeGameProps> = ({
  className,
  leagueUrl,
  game,
  withTopRadius,
  isUnique,
}) => {
  // console.log('OvertimeGame rendered');
  // console.log('OvertimeGame game object:', game);
  const { gameId, title, participants, startsAt } = game
  const { date, time } = getGameDateTime(+startsAt * 1000)
  const { data: state } = useGameState({
    gameId,
    initialState: game.state,
  })
  const statisticsGameId = useSyncExternalStore(
    liveStatisticsGameIdStore.subscribe,
    liveStatisticsGameIdStore.getSnapshot,
    () => ""
  )

  const providerId = getProviderFromId(gameId)
  const isSportAllowed = LIVE_STATISTICS_SUPPORTED_SPORTS.includes(
    +game.sport.sportId
  )
  const isProviderAllowed =
    LIVE_STATISTICS_SUPPORTED_PROVIDERS.includes(providerId)
  const isInLive = !!game.live || game.statusCode === "ongoing"
  const isStatisticsAvailable = isProviderAllowed && isSportAllowed && isInLive
  const isSelectedForStatistics =
    isStatisticsAvailable && statisticsGameId === gameId

  const handleStatisticsClick = () => {
    liveStatisticsGameIdStore.setGameId(gameId)
    openModal("StatisticsModal")
  }

  const rootClassName = cx(
    "group flex mb:flex-col ds:items-center justify-between",
    "py-2 ds:px-4 mb:px-2 bg-accent-grey-850 last-of-type:rounded-b-md relative",
    className,
    {
      "first-of-type:rounded-t-md": withTopRadius || isUnique,
    }
  )
  const liveClassName = cx(
    "border-l-[2px] border-l-accent-red-500 absolute h-full",
    "left-0 top-0 bg-live-game-shadow w-[30%] group-last-of-type:rounded-b-md",
    {
      "group-first-of-type:rounded-t-md": withTopRadius,
    }
  )

  // Create participants with lazy-loaded images
  const participantsWithImages = [
    {
      name: participants[0]?.name || "",
      image: "interface/no_opponent",
      isLoading: false,
    },
    {
      name: participants[1]?.name || "",
      image: "interface/no_opponent",
      isLoading: false,
    },
  ]

  return (
    <div className={rootClassName}>
      {isInLive && <div className={liveClassName} />}
      <div className="flex items-end w-full relative z-10">
        <Href
          to={`${leagueUrl}/${gameId}`}
          className="flex items-center group/game-link"
        >
          {!isUnique &&
            participantsWithImages.map(({ name, image, isLoading }, index) => (
              <div key={name} className="relative">
                {isLoading || !image ? (
                  // Show team initials while loading or if no image
                  <div
                    className={cx(
                      "w-8 h-8 rounded-full bg-accent-grey-700 flex items-center justify-center text-xs font-semibold text-white",
                      { "-mt-2": !index, "-mb-2 -ml-2 z-20": !!index }
                    )}
                  />
                ) : (
                  <OpponentLogo
                    className={cx({
                      "-mt-2": !index,
                      "-mb-2 -ml-2 z-20": !!index,
                    })}
                    image={image}
                  />
                )}
              </div>
            ))}
          <div className={cx({ "ml-3": !isUnique })}>
            <div className="mb-[2px] flex items-baseline">
              {isInLive ? (
                <LiveLabel className="mr-1" />
              ) : (
                <>
                  <span className="text-caption-13 font-semibold text-accent-grey-100 mr-1">
                    <TimeValue time={time} />
                  </span>
                  <span className="text-caption-12 text-accent-grey-200">
                    {date}
                  </span>
                </>
              )}
            </div>
            <div className="text-caption-13 font-semibold group-hover/game-link:underline text-white">
              {title}
            </div>
          </div>
        </Href>
        {isInLive && (
          <button
            className={cx(
              "hover:text-brand-light-500 ml-auto mb:mr-1 disabled:text-accent-grey-500 disabled:cursor-not-allowed",
              {
                "text-brand-light-500": isSelectedForStatistics,
                "text-accent-grey-100": !isSelectedForStatistics,
              }
            )}
            disabled={!isStatisticsAvailable}
            onClick={handleStatisticsClick}
          >
            <Icon className="size-5" name="interface/statistics" />
          </button>
        )}
      </div>
      <div className="w-full ds:max-w-[26.25rem] mb:mt-2">
        <OvertimeMarkets game={game} />
      </div>
    </div>
  )
}

export default OvertimeGame
